import type { Perimeter } from '@atlaskit/atlassian-context/types';

import {
	type CategorizedStorageControlsCache,
	CategoryStorageType,
	type CdnFileVersion,
	type StorageControlsPatternFormat,
} from '../../../types';
import type { CacheLoader } from '../load-storage-controls-data';
import { resetLogLevelCache } from '../logger/determine-log-level';
import type { PackageLogLevel } from '../logger/log-level';

let analyticsEnabledGlobalState: boolean | undefined;
let productGlobalState: string | undefined;
let perimeterGlobalState: Perimeter | undefined;
let webStorageControlsEnabledGlobalState: boolean | undefined;
let indexedDbControlsEnabledGlobalState: boolean | undefined;
let disableWebStorageConsentChecksGlobalState: boolean | undefined;
let localConsentModeGlobalState: boolean | undefined;
let packageLogLevelGlobalState: PackageLogLevel | undefined;
let allowUncategorizedCookiesInOverridesGlobalState: boolean | undefined;
let allowUncategorizedItemsInWebStorageOverridesGlobalState: boolean | undefined;
let allowUncategorizedItemsInIndexedDBOverridesGlobalState: boolean | undefined;
let disableIndexedDbConsentChecksGlobalState: boolean | undefined;
let userIdGlobalState: string | undefined;
let cloudIdGlobalState: string | undefined;
let hasInitialized: boolean = false;
let cacheLoadedDateGlobalState: string | undefined;
let cdnFileVersionGlobalState: CdnFileVersion | undefined;
let patternFormatGlobalState: StorageControlsPatternFormat | undefined;
let resolvedFlatPatternFileFormatGlobalState: boolean | undefined;

let cookiesCacheLoader: CacheLoader | undefined;
let localStorageCacheLoader: CacheLoader | undefined;
let sessionStorageCacheLoader: CacheLoader | undefined;
let indexedDBCacheLoader: CacheLoader | undefined;

let loadCache: (
	storageType: CategoryStorageType,
) => Promise<CategorizedStorageControlsCache | undefined> = async (
	storageType: CategoryStorageType,
) => {
	switch (storageType) {
		case CategoryStorageType.Cookies:
			return await cookiesCacheLoader?.loadStorageControlsData();
		case CategoryStorageType.LocalStorage:
			return await localStorageCacheLoader?.loadStorageControlsData();
		case CategoryStorageType.SessionStorage:
			return await sessionStorageCacheLoader?.loadStorageControlsData();
		case CategoryStorageType.IndexedDB:
			return await indexedDBCacheLoader?.loadStorageControlsData();
		default:
			return undefined;
	}
};
let loadCacheMaybeSync: (
	storageType: CategoryStorageType,
) =>
	| CategorizedStorageControlsCache
	| undefined
	| Promise<CategorizedStorageControlsCache | undefined> = (storageType: CategoryStorageType) => {
	switch (storageType) {
		case CategoryStorageType.Cookies:
			return (
				cookiesCacheLoader?.getStorageControlsDataFromCache() ??
				cookiesCacheLoader?.loadStorageControlsData()
			);
		case CategoryStorageType.LocalStorage:
			return (
				localStorageCacheLoader?.getStorageControlsDataFromCache() ??
				localStorageCacheLoader?.loadStorageControlsData()
			);
		case CategoryStorageType.SessionStorage:
			return (
				sessionStorageCacheLoader?.getStorageControlsDataFromCache() ??
				sessionStorageCacheLoader?.loadStorageControlsData()
			);
		case CategoryStorageType.IndexedDB:
			return (
				indexedDBCacheLoader?.getStorageControlsDataFromCache() ??
				indexedDBCacheLoader?.loadStorageControlsData()
			);
		default:
			return undefined;
	}
};

export interface OverrideBscGlobalStateParams {
	loadCache?: typeof loadCache;
	loadCacheMaybeSync?: typeof loadCacheMaybeSync;
	product?: typeof productGlobalState;
}

export interface BscGlobalState {
	analyticsEnabled?: boolean;
	localConsentMode?: boolean;
	product?: string;
	perimeter?: Perimeter;

	webStorageEnabled?: boolean;
	indexedDbEnabled?: boolean;
	/**
	 * When `true` and `webStorageEnabled` is `true`, the web-storage passthrough installed for
	 * `localStorage` / `sessionStorage` runs in analytics-only mode: it still emits the usual
	 * analytics events for `setItem` calls, but it never blocks writes and the behaviour of
	 * `AtlBrowserStorage` is unchanged.
	 *
	 * Use this for telemetry-first rollouts where you want to measure usage of the controls
	 * pipeline without enforcing consent yet. This is the web-storage analogue of
	 * `disableIndexedDbConsentChecks`.
	 *
	 * Has no effect when `webStorageEnabled` is `false` (the passthrough is not installed).
	 *
	 * @default false
	 */
	disableWebStorageConsentChecks?: boolean;
	/**
	 * **Scope: cookies AND web storage** (despite the name).
	 *
	 * When `true`, both the `document.cookie` setter override AND the web-storage
	 * (`localStorage` / `sessionStorage`) `setItem` overrides will allow
	 * uncategorized items to pass through.
	 *
	 * If you only want to allow uncategorized writes for web storage without
	 * affecting cookies, use `allowUncategorizedItemsInWebStorageOverrides`
	 * instead.
	 *
	 * @default false
	 */
	allowUncategorizedCookiesInOverrides?: boolean;
	/**
	 * **Scope: web storage ONLY.**
	 *
	 * When `true` and `webStorageEnabled` is `true`, web-storage (`localStorage` /
	 * `sessionStorage`) `setItem` overrides will allow uncategorized items to pass
	 * through, the same way `allowUncategorizedCookiesInOverrides` does for web
	 * storage.
	 *
	 * Unlike `allowUncategorizedCookiesInOverrides` — which acts on **both**
	 * cookies and web storage — this flag has **no effect on cookies**. The
	 * `document.cookie` setter override never reads this value. Use this when you
	 * need to allow uncategorized writes for web storage without changing cookie
	 * behaviour.
	 *
	 * The two flags are independent and additive: web storage allows
	 * uncategorized writes when EITHER flag is `true`.
	 *
	 * Has no effect when `webStorageEnabled` is `false` (the passthrough is not
	 * installed).
	 *
	 * @default false
	 */
	allowUncategorizedItemsInWebStorageOverrides?: boolean;
	/**
	 * **Scope: IndexedDB ONLY.**
	 *
	 * IDB-only counterpart to `allowUncategorizedItemsInWebStorageOverrides`.
	 *
	 * When `true` and `indexedDbEnabled` is `true`, IndexedDB `open()` overrides
	 * will allow uncategorized databases to pass through instead of being blocked.
	 *
	 * Has **no effect on cookies or web storage** — only the IndexedDB open
	 * override reads this value.
	 *
	 * Has no effect when `indexedDbEnabled` is `false` (the passthrough is not
	 * installed).
	 *
	 * @default false
	 */
	allowUncategorizedItemsInIndexedDBOverrides?: boolean;
	disableIndexedDbConsentChecks?: boolean;

	packageLogLevel?: PackageLogLevel;
	cookiesCacheLoader?: CacheLoader;
	localStorageCacheLoader?: CacheLoader;
	sessionStorageCacheLoader?: CacheLoader;
	indexedDBCacheLoader?: CacheLoader;
	hasInitialized?: boolean;
	userId?: string;
	cloudId?: string;
	cacheLoadedDate?: string;
	cdnFileVersion?: CdnFileVersion;
	/**
	 * Which category-file layout this process fetches — the fetch decision, made once by whichever
	 * caller needs it first and never revised.
	 *
	 * Held here because `fg` is not stable while the flag system is still resolving, so deciding
	 * separately at each site that needs to know (the cache loaders, the SSR URL builder) can produce
	 * disagreeing answers within one process. Nothing re-decides afterwards, including
	 * `initializeControls`: once a payload has been fetched in one layout, revising the answer would
	 * leave later readers interpreting it as the other.
	 *
	 * This is not the same thing as a payload's `patternFormat`, which describes the bytes of one
	 * fetched file. `undefined` until something decides.
	 *
	 * TEMPORARY: Will be removed when `platform_bsc_ssr_flat_pattern_file_format` is cleaned up.
	 */
	patternFormat?: StorageControlsPatternFormat;

	loadCache: (
		storageType: CategoryStorageType,
	) => Promise<CategorizedStorageControlsCache | undefined>;
	loadCacheMaybeSync: (
		storageType: CategoryStorageType,
	) =>
		| CategorizedStorageControlsCache
		| undefined
		| Promise<CategorizedStorageControlsCache | undefined>;
}

export const getBscGlobalState: () => BscGlobalState = () => {
	return {
		analyticsEnabled: analyticsEnabledGlobalState ?? true,
		product: productGlobalState,
		perimeter: perimeterGlobalState,
		webStorageEnabled: webStorageControlsEnabledGlobalState ?? false,
		indexedDbEnabled: indexedDbControlsEnabledGlobalState ?? false,
		disableWebStorageConsentChecks: disableWebStorageConsentChecksGlobalState ?? false,
		localConsentMode: localConsentModeGlobalState ?? false,
		allowUncategorizedCookiesInOverrides: allowUncategorizedCookiesInOverridesGlobalState ?? false,
		allowUncategorizedItemsInWebStorageOverrides:
			allowUncategorizedItemsInWebStorageOverridesGlobalState ?? false,
		allowUncategorizedItemsInIndexedDBOverrides:
			allowUncategorizedItemsInIndexedDBOverridesGlobalState ?? false,
		disableIndexedDbConsentChecks: disableIndexedDbConsentChecksGlobalState ?? false,
		packageLogLevel: packageLogLevelGlobalState,
		cookiesCacheLoader,
		localStorageCacheLoader,
		sessionStorageCacheLoader,
		indexedDBCacheLoader,
		loadCache,
		loadCacheMaybeSync,
		hasInitialized,
		userId: userIdGlobalState,
		cloudId: cloudIdGlobalState,
		cacheLoadedDate: cacheLoadedDateGlobalState,
		cdnFileVersion: cdnFileVersionGlobalState,
		patternFormat: patternFormatGlobalState,
	};
};

export const testing_overrideGlobalState = ({
	loadCache: overrideLoadCache,
	loadCacheMaybeSync: overrideLoadCacheMaybeSync,
	product: overrideProduct,
}: OverrideBscGlobalStateParams): void => {
	if (overrideLoadCache) {
		loadCache = overrideLoadCache;
	}
	if (overrideLoadCacheMaybeSync) {
		loadCacheMaybeSync = overrideLoadCacheMaybeSync;
	}
	if (overrideProduct) {
		productGlobalState = overrideProduct;
	}
};

export const clearGlobalState = (): void => {
	analyticsEnabledGlobalState = undefined;
	productGlobalState = undefined;
	webStorageControlsEnabledGlobalState = undefined;
	indexedDbControlsEnabledGlobalState = undefined;
	disableWebStorageConsentChecksGlobalState = undefined;
	localConsentModeGlobalState = undefined;
	allowUncategorizedCookiesInOverridesGlobalState = undefined;
	allowUncategorizedItemsInWebStorageOverridesGlobalState = undefined;
	allowUncategorizedItemsInIndexedDBOverridesGlobalState = undefined;
	disableIndexedDbConsentChecksGlobalState = undefined;
	userIdGlobalState = undefined;
	cloudIdGlobalState = undefined;
	packageLogLevelGlobalState = undefined;
	// Reset the resolved-log-level cache too, so a teardown that clears global state without a
	// following initializeGlobalState() can't bleed the previous request's level into the next.
	resetLogLevelCache();

	cookiesCacheLoader = undefined;
	localStorageCacheLoader = undefined;
	sessionStorageCacheLoader = undefined;
	indexedDBCacheLoader = undefined;
	hasInitialized = false;
	cacheLoadedDateGlobalState = undefined;
	cdnFileVersionGlobalState = undefined;
	patternFormatGlobalState = undefined;
	resolvedFlatPatternFileFormatGlobalState = undefined;
};

/**
 * Internal: used only by initialize-global-state to mutate state.
 * Avoids circular dependency by keeping load-storage-controls-data and logger out of core.
 */
export interface InternalStateUpdates {
	productGlobalState?: string;
	webStorageControlsEnabledGlobalState?: boolean;
	indexedDbControlsEnabledGlobalState?: boolean;
	disableWebStorageConsentChecksGlobalState?: boolean;
	allowUncategorizedCookiesInOverridesGlobalState?: boolean;
	allowUncategorizedItemsInWebStorageOverridesGlobalState?: boolean;
	allowUncategorizedItemsInIndexedDBOverridesGlobalState?: boolean;
	disableIndexedDbConsentChecksGlobalState?: boolean;
	localConsentModeGlobalState?: boolean;
	analyticsEnabledGlobalState?: boolean;
	perimeterGlobalState?: Perimeter;
	cookiesCacheLoader?: CacheLoader;
	localStorageCacheLoader?: CacheLoader;
	sessionStorageCacheLoader?: CacheLoader;
	indexedDBCacheLoader?: CacheLoader;
	packageLogLevelGlobalState?: PackageLogLevel;
	hasInitialized?: boolean;
	cloudIdGlobalState?: string;
	userIdGlobalState?: string;
	cdnFileVersionGlobalState?: CdnFileVersion;
}

export function __internalApplyInitialState(updates: InternalStateUpdates): void {
	if (updates.productGlobalState !== undefined) {
		productGlobalState = updates.productGlobalState;
	}
	if (updates.webStorageControlsEnabledGlobalState !== undefined) {
		webStorageControlsEnabledGlobalState = updates.webStorageControlsEnabledGlobalState;
	}
	if (updates.indexedDbControlsEnabledGlobalState !== undefined) {
		indexedDbControlsEnabledGlobalState = updates.indexedDbControlsEnabledGlobalState;
	}
	if (updates.disableWebStorageConsentChecksGlobalState !== undefined) {
		disableWebStorageConsentChecksGlobalState = updates.disableWebStorageConsentChecksGlobalState;
	}
	if (updates.allowUncategorizedCookiesInOverridesGlobalState !== undefined) {
		allowUncategorizedCookiesInOverridesGlobalState =
			updates.allowUncategorizedCookiesInOverridesGlobalState;
	}
	if (updates.allowUncategorizedItemsInWebStorageOverridesGlobalState !== undefined) {
		allowUncategorizedItemsInWebStorageOverridesGlobalState =
			updates.allowUncategorizedItemsInWebStorageOverridesGlobalState;
	}
	if (updates.allowUncategorizedItemsInIndexedDBOverridesGlobalState !== undefined) {
		allowUncategorizedItemsInIndexedDBOverridesGlobalState =
			updates.allowUncategorizedItemsInIndexedDBOverridesGlobalState;
	}
	if (updates.disableIndexedDbConsentChecksGlobalState !== undefined) {
		disableIndexedDbConsentChecksGlobalState = updates.disableIndexedDbConsentChecksGlobalState;
	}
	if (updates.localConsentModeGlobalState !== undefined) {
		localConsentModeGlobalState = updates.localConsentModeGlobalState;
	}
	if (updates.analyticsEnabledGlobalState !== undefined) {
		analyticsEnabledGlobalState = updates.analyticsEnabledGlobalState;
	}
	if (updates.perimeterGlobalState !== undefined) {
		perimeterGlobalState = updates.perimeterGlobalState;
	}
	if (updates.cookiesCacheLoader !== undefined) {
		cookiesCacheLoader = updates.cookiesCacheLoader;
	}
	if (updates.localStorageCacheLoader !== undefined) {
		localStorageCacheLoader = updates.localStorageCacheLoader;
	}
	if (updates.sessionStorageCacheLoader !== undefined) {
		sessionStorageCacheLoader = updates.sessionStorageCacheLoader;
	}
	if (updates.indexedDBCacheLoader !== undefined) {
		indexedDBCacheLoader = updates.indexedDBCacheLoader;
	}
	if (updates.packageLogLevelGlobalState !== undefined) {
		packageLogLevelGlobalState = updates.packageLogLevelGlobalState;
	}
	if (updates.hasInitialized !== undefined) {
		hasInitialized = updates.hasInitialized;
	}
	if (updates.cloudIdGlobalState !== undefined) {
		cloudIdGlobalState = updates.cloudIdGlobalState;
	}
	if (updates.userIdGlobalState !== undefined) {
		userIdGlobalState = updates.userIdGlobalState;
	}
	if (updates.cdnFileVersionGlobalState !== undefined) {
		cdnFileVersionGlobalState = updates.cdnFileVersionGlobalState;
	}
}

/**
 * The category-file layout this process fetches, or `undefined` if nothing has decided it yet.
 *
 * TEMPORARY: Will be removed when `platform_bsc_ssr_flat_pattern_file_format` is cleaned up.
 */
export function __internalGetPatternFormat(): StorageControlsPatternFormat | undefined {
	return patternFormatGlobalState;
}

/**
 * Records which category-file layout this process fetches, the first time anything decides.
 *
 * Write-once, like `__internalSetCacheLoadedDate`. Nothing may revise the decision afterwards: once a
 * payload has been fetched in one layout, changing the answer would leave later readers interpreting
 * it as the other. `getFetchPatternFormat` is the only caller.
 *
 * TEMPORARY: Will be removed when `platform_bsc_ssr_flat_pattern_file_format` is cleaned up.
 */
export function __internalSetPatternFormat(patternFormat: StorageControlsPatternFormat): void {
	if (patternFormatGlobalState === undefined) {
		patternFormatGlobalState = patternFormat;
	}
}

/**
 * The Confluence SSR flag value resolved outside this package, or `undefined` if it has not been
 * provided yet.
 *
 * TEMPORARY: Will be removed when `platform_bsc_ssr_flat_pattern_file_format` is cleaned up.
 */
export function __internalGetResolvedFlatPatternFileFormat(): boolean | undefined {
	return resolvedFlatPatternFileFormatGlobalState;
}

/**
 * Records the first Confluence-resolved SSR flag value. Later values cannot revise the seed.
 *
 * TEMPORARY: Will be removed when `platform_bsc_ssr_flat_pattern_file_format` is cleaned up.
 */
export function __internalSetResolvedFlatPatternFileFormat(value: boolean): void {
	if (resolvedFlatPatternFileFormatGlobalState === undefined) {
		resolvedFlatPatternFileFormatGlobalState = value;
	}
}

export function __internalGetHasInitialized(): boolean {
	return hasInitialized;
}

export function __internalGetProductGlobalState(): string | undefined {
	return productGlobalState;
}

export function __internalGetWebStorageControlsEnabledGlobalState(): boolean | undefined {
	return webStorageControlsEnabledGlobalState;
}

export function __internalGetIndexedDbControlsEnabledGlobalState(): boolean | undefined {
	return indexedDbControlsEnabledGlobalState;
}

export function __internalGetDisableWebStorageConsentChecksGlobalState(): boolean | undefined {
	return disableWebStorageConsentChecksGlobalState;
}

export function __internalGetAllowUncategorizedCookiesInOverridesGlobalState():
	| boolean
	| undefined {
	return allowUncategorizedCookiesInOverridesGlobalState;
}

export function __internalGetAllowUncategorizedItemsInWebStorageOverridesGlobalState():
	| boolean
	| undefined {
	return allowUncategorizedItemsInWebStorageOverridesGlobalState;
}

export function __internalGetAllowUncategorizedItemsInIndexedDBOverridesGlobalState():
	| boolean
	| undefined {
	return allowUncategorizedItemsInIndexedDBOverridesGlobalState;
}

export function __internalSetCacheLoadedDate(date: string): void {
	if (cacheLoadedDateGlobalState === undefined) {
		cacheLoadedDateGlobalState = date;
	}
}

export function __internalSetCdnFileVersion(version: CdnFileVersion): void {
	if (cdnFileVersionGlobalState === undefined) {
		cdnFileVersionGlobalState = version;
	}
}

export function __internalGetDisableIndexedDbConsentChecksGlobalState(): boolean | undefined {
	return disableIndexedDbConsentChecksGlobalState;
}
